/**
 * TikTokEditOverlayWidget — portal-based widget that renders the FloatingSavePill.
 *
 * Added as the last section in the TikTok Listing DocViewSchema.
 * Uses createPortal to render the fixed-position pill outside the scroll container.
 * Also handles store initialization from formData.
 */

import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import type { ExperienceWidgetProps } from "@/lib/views/widgets/registry";
import { getChannelConfig } from "./shared";
import { useTikTokEditStore, selectIsReadOnly } from "../stores/useTikTokEditStore";
import { FloatingSavePill } from "./FloatingSavePill";

export function TikTokEditOverlayWidget({ section, formData }: ExperienceWidgetProps) {
  const channel = section.description || "";
  const isTikTok = channel === "TikTok Shop";
  const initRef = useRef(false);

  const config = getChannelConfig(channel);
  const init = useTikTokEditStore((s) => s.init);
  const currentProductId = useTikTokEditStore((s) => s._initializedFor);

  // Initialize the edit store when formData arrives
  useEffect(() => {
    if (!isTikTok || !formData) return;

    const productId = formData[config.productId] as string;
    const connectionName = formData[config.connection] as string;
    const listingName = formData.name as string;

    // Guard: only init when we have the required fields
    if (!productId || !connectionName || !listingName) return;

    // Guard: don't re-init for the same product
    if (currentProductId === productId) return;

    init(formData, connectionName, productId, listingName);
    initRef.current = true;
  }, [isTikTok, formData, config, init, currentProductId]);

  const isReadOnly = useTikTokEditStore(selectIsReadOnly);

  // Don't render save pill for non-TikTok or read-only products
  if (!isTikTok || isReadOnly) return null;

  // Portal the floating pill to document.body so it's outside scroll containers
  return createPortal(<FloatingSavePill />, document.body);
}
